import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { Ban, Play, Search, Loader2, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

type EstadoClinica = 'activa' | 'suspendida' | 'eliminada';

function getInitials(nombre: string) {
  return (nombre ?? '').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
}

const TAB_CLS = [
  'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
  'text-slate-400 border-transparent hover:text-slate-200',
  'data-[state=active]:text-amber-400 data-[state=active]:border-amber-400',
].join(' ');

function MedicosTable({
  medicos,
  estadoClinica,
  onCambiarEstado,
  isPending,
}: {
  medicos: any[];
  estadoClinica: EstadoClinica;
  onCambiarEstado: (id: string, activo: boolean) => void;
  isPending: boolean;
}) {
  const [search, setSearch] = useState('');

  const filtered = medicos.filter(m => {
    const q = search.toLowerCase();
    return (
      m.nombre?.toLowerCase().includes(q) ||
      m.usuario?.email?.toLowerCase().includes(q) ||
      m.clinica?.nombre?.toLowerCase().includes(q) ||
      m.especialidad?.nombre?.toLowerCase().includes(q) ||
      m.cedulaProfesional?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4 pt-4">
      {/* Búsqueda */}
      <div className="relative w-full max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          placeholder="Buscar por nombre, email, clínica, cédula…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-700 bg-slate-900 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-600">
          {search ? 'No hay coincidencias' : 'Sin médicos en esta categoría'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-900 border-b border-slate-800">
              <tr>
                {['Médico', 'Teléfono', 'Cédula', 'Clínica', 'Especialidad', 'Estado médico',
                  ...(estadoClinica === 'activa' ? ['Acciones'] : [])
                ].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.map(m => {
                const activoUsuario = m.usuario?.activo !== false;
                const rowOpacity = estadoClinica !== 'activa' ? 'opacity-60' : '';
                return (
                  <tr key={m.id} className={`bg-slate-950 hover:bg-slate-900 transition-colors ${rowOpacity}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {m.usuario?.fotoUrl ? (
                          <img src={m.usuario.fotoUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                            {getInitials(m.nombre)}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white">{m.nombre}</p>
                          <p className="text-xs text-slate-500">{m.usuario?.email ?? '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{m.usuario?.telefono ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs font-mono">{m.cedulaProfesional ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{m.clinica?.nombre ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{m.especialidad?.nombre ?? '—'}</td>
                    <td className="px-4 py-3">
                      {activoUsuario ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-emerald-900 text-emerald-300 border-emerald-700">
                          <UserCheck size={11} /> Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-amber-900 text-amber-300 border-amber-700">
                          <UserX size={11} /> Suspendido
                        </span>
                      )}
                    </td>
                    {estadoClinica === 'activa' && (
                      <td className="px-4 py-3">
                        {activoUsuario ? (
                          <button
                            onClick={() => onCambiarEstado(m.id, false)}
                            disabled={isPending}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-400 border border-amber-800 hover:bg-amber-900/30 transition-colors disabled:opacity-50"
                          >
                            <Ban size={13} /> Suspender
                          </button>
                        ) : (
                          <button
                            onClick={() => onCambiarEstado(m.id, true)}
                            disabled={isPending}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-emerald-400 border border-emerald-800 hover:bg-emerald-900/30 transition-colors disabled:opacity-50"
                          >
                            <Play size={13} /> Reactivar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SuperAdminMedicosPage() {
  const qc = useQueryClient();

  const { data: medicos = [], isLoading } = useQuery<any[]>({
    queryKey: ['sa-medicos'],
    queryFn: () => api.get('/api/super-admin/medicos').then(r => r.data),
    staleTime: 0,
  });

  const estadoMut = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api.patch(`/api/super-admin/medicos/${id}/estado`, { activo }).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sa-medicos'] });
      toast.success(vars.activo ? 'Médico reactivado' : 'Médico suspendido');
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error')),
  });

  const byEstado = (ec: EstadoClinica) =>
    medicos.filter(m => (m.clinica?.estadoClinica ?? 'activa') === ec);

  const activos     = byEstado('activa');
  const suspendidos = byEstado('suspendida');
  const eliminados  = byEstado('eliminada');

  const tabs: { value: EstadoClinica; label: string; count: number; countCls: string }[] = [
    { value: 'activa',     label: 'Activas',     count: activos.length,     countCls: 'bg-emerald-900 text-emerald-300' },
    { value: 'suspendida', label: 'Suspendidas', count: suspendidos.length, countCls: 'bg-amber-900 text-amber-300' },
    { value: 'eliminada',  label: 'Eliminadas',  count: eliminados.length,  countCls: 'bg-red-900 text-red-400' },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-white">Médicos</h1>
        <p className="text-slate-400 text-sm mt-0.5">{medicos.length} médicos en toda la plataforma</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-500" size={28} /></div>
      ) : (
        <Tabs.Root defaultValue="activa">
          <Tabs.List className="flex border-b border-slate-800 gap-1 overflow-x-auto">
            {tabs.map(t => (
              <Tabs.Trigger key={t.value} value={t.value} className={TAB_CLS}>
                {t.label}
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${t.countCls}`}>
                  {t.count}
                </span>
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {tabs.map(t => (
            <Tabs.Content key={t.value} value={t.value}>
              <MedicosTable
                medicos={t.value === 'activa' ? activos : t.value === 'suspendida' ? suspendidos : eliminados}
                estadoClinica={t.value}
                onCambiarEstado={(id, activo) => estadoMut.mutate({ id, activo })}
                isPending={estadoMut.isPending}
              />
            </Tabs.Content>
          ))}
        </Tabs.Root>
      )}
    </div>
  );
}
